import React from 'react'
import { List, ListItem, Stack, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { ChargingCardModel } from '../api/models/charging-card-model'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { getChargingCardLabel } from '../utils/get-charging-card-label'
import { ChargingCardStatusChip } from '../../shared/components/ChargingCardStatusChip'
import { LinkedWallboxes } from './LinkedWallboxes'
import useModals from 'src/Hooks/useModals'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import { useHagerBridgePermissions } from '../../shared'
import { ChargingCardLinkedWallboxesChip } from './ChargingCardLinkedWallboxesChip'
import { format } from '@e3dc-react/shell/Libs/date'

const useStyles = makeStyles()(theme => ({
  listItem: {
    paddingLeft: 0,
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
  },
  listItemTitle: {
    hyphens: 'auto',
    minWidth: '40%',
    overflowWrap: 'anywhere',
  },
  listItemValue: {
    hyphens: 'auto',
    maxWidth: '60%',
    overflowWrap: 'anywhere',
  },
  linkedWallboxesListItem: {
    paddingLeft: 0,
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    display: 'flex',
    alignItems: 'baseline',
    overflowWrap: 'anywhere',
    hyphens: 'auto',
  },
  linkButton: {
    textTransform: 'uppercase',
    marginTop: theme.spacing(3),
    fontSize: '11px',
  },
}))

export interface ChargingCardDetailsModalProps {
  open: boolean
  onClose: () => void
  place: number
  chargingCard: ChargingCardModel
  installationId: number
  isManagedMode: boolean
  installationHasWallboxes: boolean
}

/**
 * Components shows dialog for single charging card details
 * @param chargingCard - charging card based on ChargingCardModel
 *  all other params for modal
 */
export const ChargingCardDetailsModal: React.VFC<ChargingCardDetailsModalProps> = ({
  open,
  place,
  onClose,
  chargingCard,
  installationId,
  isManagedMode,
  installationHasWallboxes,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { openEditChargingCardDialog, openChargingCardToWallboxLinkModal, openUnmanagedModeOverviewDialog } = useModals()
  const { permissions } = useHagerBridgePermissions()

  const chargingCardLabel = getChargingCardLabel({ rfid: chargingCard.tokenId, cardName: chargingCard.cardName, t })

  const onOpenLinkWallboxesDialog = (): void => {
    isManagedMode
      ? openChargingCardToWallboxLinkModal({
          installationId,
        })
      : openUnmanagedModeOverviewDialog({ installationId })
  }

  return (
    <Dialog
      title={t('emobility.charging-cards.manage-charging-card-details-dialog-title', { cardLabel: chargingCardLabel })}
      onClose={onClose}
      place={place}
      actions={{
        primary: {
          name: t('common.edit'),
          onClick: () => {
            onClose()
            openEditChargingCardDialog({ chargingCard })
          },
        },
        secondary: {
          name: t('common.close'),
        },
      }}
      maxWidth="sm"
    >
      <List>
        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingCards.cardName')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {chargingCard.cardName}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.charging-cards.charging-card-details-rfid')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {chargingCard.tokenId}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingCards.edit.user')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {chargingCard.user}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingCards.status.status')}
          </Typography>

          <ChargingCardStatusChip status={chargingCard.status} />
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingCards.created')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {format(new Date(chargingCard.createdAt), t('common.format.date-fns-date'))}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingCards.changed')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {format(new Date(chargingCard.changedAt), t('common.format.date-fns-date'))}
          </Typography>
        </ListItem>

        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingCards.expiration-date')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {chargingCard.expirationDate ? format(new Date(chargingCard.expirationDate), t('common.format.date-fns-date')) : '-'}
          </Typography>
        </ListItem>

        <ListItem className={classes.linkedWallboxesListItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingCards.works-on')}
          </Typography>

          <Stack flexDirection="column">
            {isManagedMode ? null : <ChargingCardLinkedWallboxesChip cardUsability="allWallboxes" />}
            {isManagedMode && chargingCard.linkedWallboxes.length === 0 ? <ChargingCardLinkedWallboxesChip cardUsability="noWallboxes" /> : null}
            {chargingCard.linkedWallboxes.length > 0 ? <LinkedWallboxes linkedWallboxes={chargingCard.linkedWallboxes} /> : null}

            {permissions.chargingCardPermissions.linkChargingCards && installationHasWallboxes && (
              <LinkButton
                clickable
                content={t('emobility.chargingCards.link-wallboxes')}
                onClick={() => {
                  onClose()
                  onOpenLinkWallboxesDialog()
                }}
                hasUnderline={false}
                linkClassName={classes.linkButton}
              />
            )}
          </Stack>
        </ListItem>
      </List>
    </Dialog>
  )
}
